/**
 * Toast transiente compartilhado (o app não tem infra global de toast):
 * `useTransientToast(variant, duration)` devolve `showToast(msg)` e o nó
 * `toast` — renderize `toast` UMA vez no componente que sobrevive ao conteúdo
 * (lista, página). Variantes: 'info' (neutro, role=status), 'success' (neutro
 * + check, role=status) e 'error' (destrutivo + alerta, role=alert).
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Check, CircleAlert } from 'lucide-react';

export type TransientToastVariant = 'error' | 'info' | 'success';

export function useTransientToast(
  variant: TransientToastVariant = 'info',
  duration = 3500
): {
  toast: ReactNode;
  showToast: (message: string) => void;
} {
  const [message, setMessage] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showToast = useCallback(
    (next: string) => {
      setMessage(next);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setMessage(null), duration);
    },
    [duration]
  );
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );
  const toast = message !== null ? <TransientToast variant={variant} message={message} /> : null;
  return { toast, showToast };
}

function TransientToast({
  variant,
  message,
}: {
  variant: TransientToastVariant;
  message: string;
}) {
  // z-[60]: acima do overlay dos dialogs (z-50) — o toast pode disparar com
  // um dialog ainda aberto (ex.: 'Senha copiada' no cofre).
  const base =
    'fixed bottom-6 left-1/2 z-[60] flex max-w-[90vw] -translate-x-1/2 items-center gap-2 rounded-md px-3.5 py-2 text-sm font-medium shadow-lg';
  if (variant === 'error') {
    return (
      <div role="alert" className={`${base} bg-destructive text-white`}>
        <CircleAlert className="size-4 shrink-0" />
        {message}
      </div>
    );
  }
  return (
    <div role="status" aria-live="polite" className={`${base} bg-foreground text-background`}>
      {variant === 'success' ? (
        <Check className="size-4 shrink-0 text-primary" strokeWidth={2.5} />
      ) : null}
      {message}
    </div>
  );
}
